import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Drawer, Group, Select, Stack, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useCreateAdminUser, useUpdateAdminUser } from '../../queries/admin-users';
import { NewUserCredentialsPanel } from './NewUserCredentialsPanel';
import { UserAppAccess } from './UserAppAccess';
import { ApiError } from '../../lib/api-error';
import type { SystemRole, User } from '../../types/api';

const schema = z.object({
  username: z
    .string()
    .min(3)
    .max(64)
    .regex(/^[A-Za-z0-9_.-]+$/, { message: 'letters, digits, _ . -' }),
  email: z.string().email().max(255),
  system_role: z.enum(['superadmin', 'admin', 'user']),
});
type FormValues = z.infer<typeof schema>;

interface Props {
  opened: boolean;
  onClose: () => void;
  user?: User | null;
}

const SYSTEM_ROLE_OPTIONS: { value: SystemRole; label: string }[] = [
  { value: 'user', label: 'user' },
  { value: 'admin', label: 'admin' },
  { value: 'superadmin', label: 'superadmin' },
];

export function UserFormDrawer({ opened, onClose, user }: Props) {
  const isEdit = !!user;
  const createM = useCreateAdminUser();
  const updateM = useUpdateAdminUser(user?.id ?? '');

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { username: '', email: '', system_role: 'user' },
  });

  // Panel state: after successful create, hold the temp password for the modal-like view
  const [credentials, setCredentials] = useState<{ user: User; temp: string } | null>(null);
  const [lastOpenKey, setLastOpenKey] = useState<string | null>(null);

  // Reset form + panel each time the drawer reopens (React-19 adjust-during-render).
  const currentKey = opened ? `${user?.id ?? 'new'}:open` : 'closed';
  if (lastOpenKey !== currentKey) {
    setLastOpenKey(currentKey);
    if (opened) {
      setCredentials(null);
      if (user) {
        form.reset({ username: user.username, email: user.email, system_role: user.system_role });
      } else {
        form.reset({ username: '', email: '', system_role: 'user' });
      }
    }
  }

  async function onSubmit(values: FormValues) {
    try {
      if (isEdit) {
        await updateM.mutateAsync(values);
        notifications.show({ title: 'User updated', message: values.username, color: 'pine' });
        onClose();
      } else {
        const res = await createM.mutateAsync(values);
        setCredentials({ user: res.user, temp: res.temp_password });
      }
    } catch (err) {
      notifications.show({
        title: 'Save failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      padding="md"
      title={
        <Title order={4}>
          {isEdit ? 'Edit user' : credentials ? 'New user created' : 'New user'}
        </Title>
      }
    >
      {credentials ? (
        <NewUserCredentialsPanel
          user={credentials.user}
          tempPassword={credentials.temp}
          onDone={() => {
            setCredentials(null);
            onClose();
          }}
        />
      ) : (
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Stack gap="sm">
            <TextInput
              label="Username"
              required
              {...form.register('username')}
              error={form.formState.errors.username?.message}
            />
            <TextInput
              label="Email"
              required
              {...form.register('email')}
              error={form.formState.errors.email?.message}
            />
            <Controller
              control={form.control}
              name="system_role"
              render={({ field }) => (
                <Select
                  label="System role"
                  data={SYSTEM_ROLE_OPTIONS}
                  value={field.value}
                  onChange={(v) => field.onChange((v ?? 'user') as SystemRole)}
                />
              )}
            />
            {isEdit && user && <UserAppAccess userId={user.id} />}
            <Group justify="flex-end" mt="sm">
              <Button variant="default" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" loading={form.formState.isSubmitting}>
                {isEdit ? 'Save changes' : 'Create user'}
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Drawer>
  );
}
